import * as Dfns from 'date-fns/fp';
import { DateGroupingKey } from '@blisspointmedia/bpm-types/dist/FiscalCalendar';
import {
  Brand60,
  BrandEquityLight,
  Channel14b,
  Neutral400,
  Neutral500,
  shadeColor
} from '@src/utils/colors';
import { groupChartDataByDate } from '../../BrandEquity/BrandEquityUtils';
import {
  calculateNormalizedWeights,
  EnabledModelWithCsvType,
  getRecentWeeksDateSet,
  MEAN_METRIC,
  StackedCSVRow,
  toIsoDate,
  TTM_WEEKS
} from './hroasUtils';
import { ModelFamily } from './slots';

export interface HistoricalRevenueDataPoint {
  date: string;
  [seriesKey: string]: string | number | null;
}

export type ChartAxis = 'left' | 'right';

export interface ChartModel {
  id: string;
  label: string;
  enabled: boolean;
  family: ModelFamily;
}

export interface ChartSeries {
  id: string;
  label: string;
  axis: ChartAxis;
  color: string;
}

export type ActiveMap = Record<string, boolean>;

export const AXES: ChartAxis[] = ['left', 'right'];

export const PREDICTED_KEY: Record<ChartAxis, string> = {
  left: 'predicted:left',
  right: 'predicted:right'
};

export const PREDICTED_LINE_STYLE: Record<ChartAxis, { color: string; opacity: number }> = {
  left: { color: Brand60, opacity: 1 },
  right: { color: Channel14b, opacity: 0.5 }
};

export const actualKey = (id: string): string => `actual:${id}`;
export const predictedKey = (id: string): string => `predicted:${id}`;
export const stackedKey = (id: string): string => `stacked:${id}`;
export const dynamicKey = (axis: ChartAxis): string => `dynamic:${axis}`;

const FAMILY_ORDER: ModelFamily[] = ['rmmm', 'geommm', 'brand_equity'];

const FAMILY_COLOR: Record<ModelFamily, string> = {
  rmmm: Neutral500,
  geommm: Neutral400,
  brand_equity: BrandEquityLight
};

// Adjacent shades alternate darker/lighter with rising amplitude so siblings
// stay visually distinct from each other even at pale or low-contrast base
// hues — going all-lighter or all-darker collapses adjacent shades toward
// the same tint and makes them hard to tell apart.
export const SIBLING_SHADE_STEPS = [-0.35, 0.4, -0.6, 0.65, -0.2, 0.25];

const isBlank = (value: string | number | null | undefined): boolean =>
  value === null || value === undefined || value === 0;

export const parseMddyyDateToYyyyMmDd = (dateStr: string): string | null => {
  try {
    const parsed = Dfns.parse(new Date(), 'M/d/yyyy', dateStr);
    if (!Dfns.isValid(parsed)) {
      const intDate = parseInt(dateStr, 10);
      if (!Number.isNaN(intDate)) {
        const excelEpoch = new Date(1899, 11, 30);
        const jsDate = new Date(excelEpoch.getTime() + intDate * 24 * 60 * 60 * 1000);
        if (Dfns.isValid(jsDate)) {
          return Dfns.format('yyyy-MM-dd', jsDate);
        }
      }
      return null;
    }
    return Dfns.format('yyyy-MM-dd', parsed);
  } catch {
    return null;
  }
};

const sumOrNull = (rows: StackedCSVRow[], field: 'actual' | 'predicted'): number | null =>
  rows.length > 0 ? rows.reduce((sum, row) => sum + (Number(row[field]) || 0), 0) : null;

export const buildHistoricalRevenueData = (
  stackedCSVData: StackedCSVRow[],
  enabledSlotIds: string[]
): HistoricalRevenueDataPoint[] => {
  if (stackedCSVData.length === 0 || enabledSlotIds.length === 0) {
    return [];
  }

  const meanRows = stackedCSVData.filter(
    row => row.metric === MEAN_METRIC && enabledSlotIds.includes(row.model_type)
  );

  const rowsByDateAndSlot: Record<string, Record<string, StackedCSVRow[]>> = {};
  meanRows.forEach(row => {
    const date = parseMddyyDateToYyyyMmDd(String(row.date));
    if (!date) {
      return;
    }
    rowsByDateAndSlot[date] ??= {};
    rowsByDateAndSlot[date][row.model_type] ??= [];
    rowsByDateAndSlot[date][row.model_type].push(row);
  });

  const result = Object.keys(rowsByDateAndSlot)
    .sort()
    .map(date => {
      const point: HistoricalRevenueDataPoint = { date };
      enabledSlotIds.forEach(id => {
        const rows = rowsByDateAndSlot[date][id] ?? [];
        point[actualKey(id)] = sumOrNull(rows, 'actual');
        point[predictedKey(id)] = sumOrNull(rows, 'predicted');
      });
      return point;
    });

  const lastPoint = result[result.length - 1];
  if (
    lastPoint &&
    enabledSlotIds.every(
      id => isBlank(lastPoint[actualKey(id)]) && isBlank(lastPoint[predictedKey(id)])
    )
  ) {
    return result.slice(0, -1);
  }
  return result;
};

export const buildChartSeries = (models: ChartModel[]): ChartSeries[] => {
  const ordered = [...models].sort(
    (a, b) => FAMILY_ORDER.indexOf(a.family) - FAMILY_ORDER.indexOf(b.family)
  );
  const seenInFamily: Partial<Record<ModelFamily, number>> = {};
  return ordered.map(model => {
    const siblingIndex = seenInFamily[model.family] ?? 0;
    seenInFamily[model.family] = siblingIndex + 1;
    const base = FAMILY_COLOR[model.family];
    return {
      id: model.id,
      label: model.label,
      axis: model.family === 'brand_equity' ? 'right' : 'left',
      color:
        siblingIndex === 0
          ? base
          : shadeColor(base, SIBLING_SHADE_STEPS[(siblingIndex - 1) % SIBLING_SHADE_STEPS.length])
    };
  });
};

export const seriesOfAxis = (series: ChartSeries[], axis: ChartAxis): ChartSeries[] =>
  series.filter(s => s.axis === axis);

export const getDefaultActive = (series: ChartSeries[], enabledIds: string[]): ActiveMap => {
  const enabled = series.filter(s => enabledIds.includes(s.id));
  const leftEnabled = enabled.some(s => s.axis === 'left');
  const active: ActiveMap = {
    [PREDICTED_KEY.left]: leftEnabled,
    [PREDICTED_KEY.right]: !leftEnabled && enabled.some(s => s.axis === 'right')
  };
  series.forEach(s => {
    active[s.id] = enabledIds.includes(s.id) && (s.axis === 'left' || !leftEnabled);
  });
  return active;
};

export const toggleLegendKey = (
  active: ActiveMap,
  key: string,
  series: ChartSeries[],
  enabledIds: string[]
): ActiveMap => {
  const predictedAxis = AXES.find(axis => PREDICTED_KEY[axis] === key);
  if (predictedAxis === 'left') {
    return seriesOfAxis(series, 'left').some(s => active[s.id])
      ? { ...active, [key]: !active[key] }
      : active;
  }
  if (predictedAxis === 'right') {
    const next = { ...active, [key]: !active[key] };
    seriesOfAxis(series, 'right')
      .filter(s => enabledIds.includes(s.id))
      .forEach(s => {
        next[s.id] = next[key];
      });
    return next;
  }
  const toggled = series.find(s => s.id === key);
  if (!toggled) {
    return active;
  }
  const next = { ...active, [key]: !active[key] };
  next[PREDICTED_KEY[toggled.axis]] = seriesOfAxis(series, toggled.axis).some(s => next[s.id]);
  return next;
};

interface SeriesConfig {
  key: string;
  isActive: boolean;
}

export const buildChartData = (
  data: HistoricalRevenueDataPoint[],
  dateGrouping: string,
  series: ChartSeries[],
  enabledIds: string[],
  active: ActiveMap
): HistoricalRevenueDataPoint[] => {
  if (data.length === 0) {
    return [];
  }

  const enabled = series.filter(s => enabledIds.includes(s.id));
  const missingValue = (axis: ChartAxis): number | null => (axis === 'left' ? 0 : null);

  const mapped: HistoricalRevenueDataPoint[] = groupChartDataByDate(
    data,
    dateGrouping as DateGroupingKey
  ).map(item => {
    const point: HistoricalRevenueDataPoint = { date: item.date };
    enabled.forEach(s => {
      point[actualKey(s.id)] = item[actualKey(s.id)] ?? missingValue(s.axis);
      point[predictedKey(s.id)] = item[predictedKey(s.id)] ?? missingValue(s.axis);
    });
    return point;
  });

  const leftEnabled = enabled.filter(s => s.axis === 'left');
  const configs: SeriesConfig[] = [
    ...enabled.flatMap(s => [
      { key: actualKey(s.id), isActive: Boolean(active[s.id]) },
      {
        key: predictedKey(s.id),
        isActive: Boolean(
          s.axis === 'left'
            ? active[s.id] || active[PREDICTED_KEY.left]
            : active[PREDICTED_KEY.right]
        )
      }
    ]),
    {
      key: PREDICTED_KEY.left,
      isActive: leftEnabled.length > 0 && Boolean(active[PREDICTED_KEY.left])
    }
  ];

  const withCombined = mapped.map(point => ({
    ...point,
    [PREDICTED_KEY.left]: leftEnabled.reduce(
      (sum, s) => sum + (Number(point[predictedKey(s.id)]) || 0),
      0
    )
  }));

  const lastIndices: Record<string, number> = {};
  for (let i = withCombined.length - 1; i >= 0; i--) {
    configs.forEach(config => {
      if (
        config.isActive &&
        !(config.key in lastIndices) &&
        !isBlank(withCombined[i][config.key])
      ) {
        lastIndices[config.key] = i;
      }
    });
  }

  const nulled = withCombined.map((point, index) => {
    const result = { ...point };
    configs.forEach(config => {
      if (config.key in lastIndices && index > lastIndices[config.key]) {
        result[config.key] = null;
      }
    });
    return result;
  });

  const end = nulled.findLastIndex(point =>
    configs.some(config => config.isActive && !isBlank(point[config.key]))
  );

  return nulled.slice(0, end + 1).map(point => {
    const result = { ...point };
    AXES.forEach(axis => {
      const axisSeries = enabled.filter(s => s.axis === axis);
      const activeSeries = axisSeries.filter(s => active[s.id]);
      const predicted = activeSeries
        .map(s => point[predictedKey(s.id)])
        .filter((value): value is number => typeof value === 'number');
      result[dynamicKey(axis)] =
        predicted.length > 0 ? predicted.reduce((sum, value) => sum + value, 0) : null;
      let running = 0;
      axisSeries.forEach(s => {
        const value = point[actualKey(s.id)];
        result[stackedKey(s.id)] = value === null ? null : running + Number(value);
        if (active[s.id]) {
          running += Number(value) || 0;
        }
      });
    });
    return result;
  });
};

export const calculateOverallTtmRevenueCredit = (
  stackedCSVData: StackedCSVRow[],
  enabledModels: EnabledModelWithCsvType[],
  brandEquityIds: string[],
  mdbeInducedEffectByDate: Map<string, number>
): number | null => {
  const enabledIds = enabledModels.map(model => model.csvType);
  const meanRows = stackedCSVData.filter(
    row => row.metric === MEAN_METRIC && enabledIds.includes(row.model_type)
  );

  const datesBySlot: Record<string, Set<string>> = {};
  meanRows.forEach(row => {
    datesBySlot[row.model_type] ??= new Set();
    datesBySlot[row.model_type].add(String(row.date));
  });
  const dateSets = Object.values(datesBySlot);
  if (dateSets.length === 0) {
    return null;
  }

  const sharedDates = new Set(
    [...dateSets[0]].filter(date => dateSets.every(set => set.has(date)))
  );
  const sharedRows = meanRows.filter(row => sharedDates.has(String(row.date)));
  const recentDates = getRecentWeeksDateSet(sharedRows, TTM_WEEKS);
  const ttmRows = sharedRows.filter(row => recentDates.has(String(row.date)));
  if (ttmRows.length === 0) {
    return null;
  }

  const weights = calculateNormalizedWeights(enabledModels);
  let weightedAttribution = 0;
  let weightedRevenue = 0;
  let brandEquityRevenue = 0;
  enabledModels.forEach(model => {
    const rows = ttmRows.filter(row => row.model_type === model.csvType);
    if (rows.length === 0) {
      return;
    }
    const isBrandEquity = brandEquityIds.includes(model.csvType);
    const revenue = rows.reduce((sum, row) => sum + (Number(row.predicted) || 0), 0);
    // Brand Equity uses its MDBE induced effect, not its own paid_attribution, to avoid
    // double-counting revenue already reattributed to real channels (DEV-21662).
    const attribution = rows.reduce(
      (sum, row) =>
        sum +
        (isBrandEquity
          ? (mdbeInducedEffectByDate.get(toIsoDate(String(row.date))) ?? 0)
          : Number(row.paid_attribution) || 0),
      0
    );
    const weight = weights.get(model.csvType) || 0;
    weightedAttribution += weight * attribution;
    weightedRevenue += weight * revenue;
    if (isBrandEquity) {
      brandEquityRevenue += revenue;
    }
  });

  const denominator = enabledIds.some(id => brandEquityIds.includes(id))
    ? brandEquityRevenue
    : weightedRevenue;
  return denominator === 0 ? null : (weightedAttribution / denominator) * 100;
};
